import React, { FC } from 'react';
import { FormikHelpers, FormikProps } from 'formik';
import { observer } from 'mobx-react';

import {
    AppSection,
    AppSectionHeader,
    InputCheckbox,
    InputText,
    Form,
    AppFooter,
    Button,
    ButtonPrimary,
    ActionList,
    ButtonOutlinePrimary
} from '../../../../components-v2/shared';

import useStore from '../../../../store/useStore';
import DefaultRoutes from '../../../../routes/DefaultRoutes';
import { Crm } from '../../../../types/Crm';
import { crmConfigTypes, unsavedChangesContainerSelectorDefault } from '../../../../constants';

export interface BackOfficeFormProps {
    initialValues: BackOfficeCrm;
    onSubmit: (values: BackOfficeCrm, actions: FormikHelpers<BackOfficeCrm>, crmType: string) => Promise<void>;
    onValidate: (values: BackOfficeCrm, crmType: string) => Promise<void>;
    enableReinitialize?: boolean;
}

interface BackOfficeCrm extends Crm {
    configuration: {
        queue_id: string;
        source_id: string;
        is_test_mode?: boolean;
    };
}

const withTestModeDefault = (values: Partial<BackOfficeCrm>): BackOfficeCrm => {
    const configuration = values.configuration || { queue_id: '', source_id: '' };
    return {
        ...values,
        configuration: {
            ...configuration,
            is_test_mode: configuration.is_test_mode ?? true
        }
    } as BackOfficeCrm;
};

export const BackOfficeForm: FC<BackOfficeFormProps> = observer(({ onSubmit, onValidate, ...props }) => {
    const { store } = useStore();
    const { router } = store;
    const { companyId } = router.params;

    const handleCancel = () => router.goTo(DefaultRoutes.CrmSelectType, { companyId }, store);

    const handleValidate = async (formikProps: FormikProps<BackOfficeCrm>) => {
        await onValidate(formikProps.values, crmConfigTypes.backoffice);
    };

    const handleSubmit = async (values: BackOfficeCrm, actions: FormikHelpers<BackOfficeCrm>) => {
        await onSubmit(withTestModeDefault(values), actions, crmConfigTypes.backoffice);
    };

    return (
        <Form
            {...props}
            initialValues={withTestModeDefault(props.initialValues)}
            onSubmit={handleSubmit}
            confirmUnsavedChanges
            unsavedChangesConfig={{
                containerQuerySelectorAll: unsavedChangesContainerSelectorDefault
            }}
        >
            {(formikProps: FormikProps<BackOfficeCrm>) => {
                return (
                    <>
                        <AppSection className="field-group-content">
                            <div className="field-group-col">
                                <AppSectionHeader title="CRM Configuration: BackOffice" />
                                <InputText
                                    name="name"
                                    label="Name"
                                    helperText="Unique name for this CRM Configuration"
                                    formikProps={formikProps}
                                    required={true}
                                    inputProps={{ maxLength: 64 }}
                                />
                            </div>
                            <div className="field-group-col">
                                <AppSectionHeader
                                    title="BackOffice Configuration"
                                    subtitle="Queue ID is the BackOffice community. Ad Source ID is the lead source. API authentication is a server secret, not a field on this form."
                                />
                                <InputText
                                    name="configuration.queue_id"
                                    label="Queue ID"
                                    helperText="BackOffice community ID the lead is submitted to"
                                    required={true}
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 32 }}
                                />
                                <InputText
                                    name="configuration.source_id"
                                    label="Ad Source ID"
                                    helperText="BackOffice lead source ID"
                                    required={true}
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 32 }}
                                />
                                <InputCheckbox
                                    name="configuration.is_test_mode"
                                    label="Test mode"
                                    formikProps={formikProps}
                                />
                            </div>
                        </AppSection>
                        <AppFooter sticky={true}>
                            <ActionList position="end">
                                {formikProps.dirty && (
                                    <Button onClick={handleCancel} data-lc-trigger-unsaved-changes={true}>
                                        Cancel
                                    </Button>
                                )}
                                <ButtonOutlinePrimary onClick={() => handleValidate(formikProps)}>
                                    Validate
                                </ButtonOutlinePrimary>
                                <ButtonPrimary type="submit" disabled={!formikProps.dirty || formikProps.isSubmitting}>
                                    {formikProps.isSubmitting ? 'Saving...' : 'Save'}
                                </ButtonPrimary>
                            </ActionList>
                        </AppFooter>
                    </>
                );
            }}
        </Form>
    );
});
